/**
 * Order-deadline computation for the ordering surfaces (right-rail indicator,
 * day badges, read-only gates).
 *
 * Wraps `@ordo/recurrence` `computeOrderDeadline` (pure, browser-safe) with
 * the school's deadline policy + a specific service day, then derives a
 * whole-day countdown. Under FIXED_WEEKLY the cutoff is week-anchored — every
 * day of a service week shares it, so passing any day of the week (callers
 * historically pass the Monday) yields the same instant. Under RELATIVE_DAYS
 * the cutoff is "N days before the service date," so each day has its own —
 * callers must pass the actual service day.
 */

import {
  computeOrderDeadline,
  type DayOfWeek,
  type OrderDeadlineMode,
  type OrderDeadlinePolicy,
} from '@ordo/recurrence'

import { addDaysKey, dateFromKey, todayKey } from './calendar'

export interface SchoolDeadlinePolicy {
  /** Absent/null ⇒ FIXED_WEEKLY (legacy callers predating the mode column). */
  orderDeadlineMode?: OrderDeadlineMode | null
  orderDeadlineDayOfWeek: DayOfWeek | null
  orderDeadlineWeeksBefore: number
  orderDeadlineTime: string
  /** RELATIVE_DAYS: calendar days before the service date (0 = same day). */
  orderDeadlineRelativeDays?: number | null
  timeZone: string
}

/**
 * Pick a school's deadline-policy fields off a wider object (e.g. an
 * `AdminSchool`) — the single place that knows which fields make up the
 * policy, so callers don't each hand-copy the list.
 */
export function schoolDeadlinePolicy(school: SchoolDeadlinePolicy): SchoolDeadlinePolicy {
  return {
    orderDeadlineMode: school.orderDeadlineMode,
    orderDeadlineDayOfWeek: school.orderDeadlineDayOfWeek,
    orderDeadlineWeeksBefore: school.orderDeadlineWeeksBefore,
    orderDeadlineTime: school.orderDeadlineTime,
    orderDeadlineRelativeDays: school.orderDeadlineRelativeDays,
    timeZone: school.timeZone,
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

export interface DeadlineStatus {
  /** The cutoff instant, or null when no deadline policy is provisioned. */
  deadline: Date | null
  /** True once `now` is at/after the deadline. */
  closed: boolean
  /** Whole days remaining until the deadline (0 when closed); null when no policy. */
  daysRemaining: number | null
}

/** Deadline + countdown for ordering against the service day `serviceDayKey`. */
export function deadlineStatus(
  serviceDayKey: string,
  policy: SchoolDeadlinePolicy,
  now: Date = new Date(),
): DeadlineStatus {
  const deadline = computeOrderDeadline(dateFromKey(serviceDayKey), {
    orderDeadlineMode: policy.orderDeadlineMode ?? 'FIXED_WEEKLY',
    orderDeadlineDayOfWeek: policy.orderDeadlineDayOfWeek,
    orderDeadlineWeeksBefore: policy.orderDeadlineWeeksBefore,
    orderDeadlineTime: policy.orderDeadlineTime,
    relativeDaysBeforeService: policy.orderDeadlineRelativeDays ?? null,
    timeZone: policy.timeZone,
  } satisfies OrderDeadlinePolicy)
  if (!deadline) {
    return { deadline: null, closed: false, daysRemaining: null }
  }
  const remainingMs = deadline.getTime() - now.getTime()
  if (remainingMs <= 0) {
    return { deadline, closed: true, daysRemaining: 0 }
  }
  return { deadline, closed: false, daysRemaining: Math.ceil(remainingMs / DAY_MS) }
}

/**
 * Deadline for the WEEK starting `mondayKey` — the cutoff after which NOTHING in
 * that week is orderable. FIXED_WEEKLY has one weekly cutoff (anchor on Monday).
 * RELATIVE_DAYS closes each day on its own, so the week as a whole closes only
 * with its LAST service day (Mon+4 = Fri) — anchoring on Monday would gray out a
 * week whose later days are still open. Mirrors the family `weekDeadlineFor`.
 */
export function weekDeadlineStatus(
  policy: SchoolDeadlinePolicy,
  mondayKey: string,
  now: Date = new Date(),
): DeadlineStatus {
  const anchorKey =
    (policy.orderDeadlineMode ?? 'FIXED_WEEKLY') === 'RELATIVE_DAYS' ? addDaysKey(mondayKey, 4) : mondayKey
  return deadlineStatus(anchorKey, policy, now)
}

/** Why a school day can't be ordered, or `null` when it's still open. */
export type OrderClosedReason = 'PAST' | 'DEADLINE'

/**
 * Whether ordering for a specific service day is closed, and why. A day
 * strictly before today is always closed. Today and future days follow the
 * day's deadline; today with NO provisioned deadline is also closed, because
 * the implicit cutoff is then the service day's local midnight (matching the
 * server's `orderingCutoff` fallback).
 */
export function orderClosedReason(
  dayKey: string,
  policy: SchoolDeadlinePolicy,
  today: string = todayKey(),
  now: Date = new Date(),
): OrderClosedReason | null {
  if (dayKey < today) {
    return 'PAST'
  }
  const status = deadlineStatus(dayKey, policy, now)
  if (status.deadline === null) {
    return dayKey === today ? 'PAST' : null
  }
  return status.closed ? 'DEADLINE' : null
}

/** How far ahead `firstOpenOrderDay` looks before giving up. */
const FIRST_OPEN_DAY_HORIZON = 60

/**
 * The earliest service day still open for ordering under the school's
 * deadline — what a date picker's `min` should be, so a closed day can't be
 * picked only to be refused at submit (the server's `assertOrderingOpen`).
 * Falls back to `today` when nothing in the horizon is open.
 */
export function firstOpenOrderDay(
  policy: SchoolDeadlinePolicy,
  today: string = todayKey(),
  now: Date = new Date(),
): string {
  for (let offset = 0; offset < FIRST_OPEN_DAY_HORIZON; offset++) {
    const day = addDaysKey(today, offset)
    if (orderClosedReason(day, policy, today, now) === null) {
      return day
    }
  }
  return today
}
